import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import Breadcrumb from "@/components/site/Breadcrumb";
import SEO from "@/components/site/SEO";
import { API_BASE, api } from "@/lib/api";
import { useSite } from "@/lib/SiteContext";
import { buildBreadcrumbs } from "@/lib/breadcrumbs";

const REQUEST_TYPES = [
  { slug: "inspection-service", name: "Inspection Service", service_slug: "quality-inspection" },
  { slug: "quality-assurance-quality-control", name: "Quality Assurance / Quality Control", service_slug: "quality-assurance-quality-control" },
  { slug: "conformity-support", name: "Conformity Support", service_slug: "conformity-assessment-support" },
  { slug: "logistics-management", name: "Logistics Management", service_slug: "logistics-management" },
  { slug: "supply-chain-solution", name: "Supply Chain Solution", service_slug: "supply-chain-solutions" },
  { slug: "medical-laboratory-equipment", name: "Medical Laboratory Equipment", service_slug: null },
  { slug: "general-request", name: "General Request", service_slug: null },
];

const ACCEPT_EXTS = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png"];
const MAX_FILE_MB = 10;

const EMPTY = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  request_type: "",
  service: "",
  product: "",
  product_other: "",
  subject: "",
  message: "",
  consent: false,
  website: "",
};

function validate(v, file) {
  const e = {};
  const nm = (v.full_name || "").trim();
  if (nm.length < 2 || nm.length > 120) e.full_name = "Full name is required (2-120 characters).";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v.email || "").trim())) e.email = "A valid email is required.";
  const ph = (v.phone || "").trim();
  if (ph.length < 5 || ph.length > 30) e.phone = "Phone number is required (5-30 characters).";
  const ct = (v.country || "").trim();
  if (ct.length < 2 || ct.length > 100) e.country = "Country is required.";
  if (!v.request_type) e.request_type = "Request type is required.";
  const sj = (v.subject || "").trim();
  if (sj.length < 2 || sj.length > 200) e.subject = "Subject is required (2-200 characters).";
  const ms = (v.message || "").trim();
  if (ms.length < 5 || ms.length > 5000) e.message = "Message must be 5-5000 characters.";
  if (!v.consent) e.consent = "Consent is required to submit.";
  if (file) {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPT_EXTS.includes(ext)) e.attachment = `Attachment must be one of: ${ACCEPT_EXTS.join(", ")}`;
    else if (file.size > MAX_FILE_MB * 1024 * 1024) e.attachment = `Attachment must be under ${MAX_FILE_MB} MB.`;
  }
  return e;
}

export default function RequestQuotePage() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { footer } = useSite();
  const breadcrumbs = buildBreadcrumbs(pathname);

  const productParam = searchParams.get("product") || "";
  const subjectParam = searchParams.get("subject") || "";

  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(null); // null | "submitting" | "ok" | "error"
  const [message, setMessage] = useState("");
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const fileRef = useRef(null);

  // Load products + services (used for the conditional selects)
  useEffect(() => {
    api.get("/products", { params: { sort: "name-asc", limit: 200, offset: 0 } })
      .then(({ data }) => setProducts(data.items || []))
      .catch(() => {});
    api.get("/services")
      .then(({ data }) => setServices(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => {});
  }, []);

  // Prefill from query params
  useEffect(() => {
    if (!productParam && !subjectParam) return;
    setValues((v) => {
      const next = { ...v };
      if (productParam) {
        next.request_type = "medical-laboratory-equipment";
        next.product = productParam;
      }
      if (subjectParam) next.subject = subjectParam;
      else if (productParam && products.length) {
        const p = products.find((x) => x.slug === productParam);
        if (p) next.subject = `Request a Quote for ${p.name}`;
      }
      return next;
    });
  }, [productParam, subjectParam, products]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const selectedType = REQUEST_TYPES.find((t) => t.slug === values.request_type);
  const showService = selectedType && selectedType.service_slug !== null;
  const showProduct = values.request_type === "medical-laboratory-equipment";

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f) {
      const err = validate(values, f).attachment;
      setErrors((prev) => ({ ...prev, attachment: err || undefined }));
    } else {
      setErrors((prev) => ({ ...prev, attachment: undefined }));
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
    setErrors((prev) => ({ ...prev, attachment: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values, file);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      // focus first errored field
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(`quote-${firstKey}`);
      if (el) el.focus();
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const fd = new FormData();
      Object.entries(values).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        fd.append(k, typeof v === "boolean" ? (v ? "true" : "false") : String(v));
      });
      if (file) fd.append("attachment", file);
      const { data } = await api.post("/quote-requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus("ok");
      setMessage("Thank you. Your quotation request has been submitted successfully. Our team will review your request and contact you using the details provided.");
    } catch (err) {
      const resp = err?.response;
      const detail = resp?.data?.detail;
      const bodyErr = resp?.data?.error;
      if (resp?.status === 400 && typeof detail === "object" && detail) {
        setErrors(detail);
        setStatus(null);
      } else if (resp?.status === 500 && (bodyErr === "email_delivery_failed" || detail?.error === "email_delivery_failed")) {
        setStatus("error");
        setMessage("We could not submit your request at this time. Please check your information and try again.");
        // Preserve values — do not clear anything.
      } else {
        setStatus("error");
        setMessage(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    }
  };

  const contactInfo = useMemo(() => {
    const cols = (footer && footer.columns) || [];
    const contactCol = cols.find((c) => (c.heading || "").toLowerCase().includes("contact"));
    return contactCol ? (contactCol.links || []) : [];
  }, [footer]);

  const seo = { title: "Request a Quote | MIST", description: "Request a quote from MIST Quality Inspection and Logistics." };

  return (
    <>
      <SEO seo={seo} />
      <section className="bg-mist-navy text-white pt-32 pb-14 lg:pt-40 lg:pb-16">
        <div className="container-mist">
          {breadcrumbs.length > 0 && <Breadcrumb items={breadcrumbs} />}
          <div className="eyebrow mb-3">Get in Touch</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.02em]">
            Request a Quote
          </h1>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="container-mist grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          <aside className="lg:col-span-1 lg:order-2 order-1">
            <p className="text-[16px] leading-relaxed text-mist-navy/80">
              Tell us about the service or equipment you require. Our team will review your request and follow up with the appropriate specialists at MIST.
            </p>
            {footer && footer.address && (
              <div className="mt-8 space-y-3 text-sm text-mist-navy/80">
                <div className="text-[11px] uppercase tracking-[0.2em] text-mist-navy/50 font-semibold mb-1">Direct Contact</div>
                <div className="whitespace-pre-line leading-relaxed" data-testid="quote-side-address">{footer.address}</div>
                {footer.email && (
                  <a href={`mailto:${footer.email}`} className="block text-mist-navy hover:text-mist-gold transition-colors">{footer.email}</a>
                )}
                {contactInfo.map((c) => (
                  <div key={c.href + c.label}>
                    <a href={c.href} className="text-mist-navy hover:text-mist-gold transition-colors">{c.label}</a>
                  </div>
                ))}
              </div>
            )}
          </aside>

          <div className="lg:col-span-2 lg:order-1 order-2">
            {status === "ok" ? (
              <div className="bg-mist-off border border-mist-soft rounded-xl p-10 text-center" data-testid="quote-success">
                <CheckCircle2 className="h-10 w-10 text-mist-gold mx-auto mb-3" aria-hidden />
                <div className="font-sans font-semibold text-2xl text-mist-navy mb-2 tracking-tight">Request received</div>
                <p className="text-sm text-mist-ink/70 max-w-md mx-auto">{message}</p>
                <button
                  type="button"
                  onClick={() => { setStatus(null); setValues(EMPTY); setFile(null); setErrors({}); }}
                  className="mt-6 inline-flex items-center gap-2 bg-mist-navy text-white px-6 h-10 text-[11px] uppercase tracking-[0.22em] font-semibold rounded-md hover:bg-mist-navy/90 transition-all duration-200"
                  data-testid="quote-reset"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="quote-form">
                {/* honeypot */}
                <input type="text" name="website" value={values.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden className="absolute w-0 h-0 opacity-0 -z-10" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field id="quote-full_name" label="Full Name" required value={values.full_name} onChange={(v) => set("full_name", v)} error={errors.full_name} />
                  <Field id="quote-company" label="Company or Organization" value={values.company} onChange={(v) => set("company", v)} error={errors.company} />
                  <Field id="quote-email" label="Email Address" type="email" required value={values.email} onChange={(v) => set("email", v)} error={errors.email} />
                  <Field id="quote-phone" label="Phone Number" type="tel" required value={values.phone} onChange={(v) => set("phone", v)} error={errors.phone} />
                  <Field id="quote-country" label="Country" required value={values.country} onChange={(v) => set("country", v)} error={errors.country} />
                  <SelectField
                    id="quote-request_type"
                    label="Request Type"
                    required
                    value={values.request_type}
                    onChange={(v) => { set("request_type", v); set("service", ""); set("product", ""); }}
                    options={[{ value: "", label: "Select a request type…" }, ...REQUEST_TYPES.map((t) => ({ value: t.slug, label: t.name }))]}
                    error={errors.request_type}
                  />
                </div>

                {showService && (
                  <SelectField
                    id="quote-service"
                    label="Service Required"
                    value={values.service}
                    onChange={(v) => set("service", v)}
                    options={[{ value: "", label: "Not specified" }, ...services.map((s) => ({ value: s.slug, label: s.title || s.name || s.slug }))]}
                  />
                )}

                {showProduct && (
                  <>
                    <SelectField
                      id="quote-product"
                      label="Medical Laboratory Product"
                      value={values.product}
                      onChange={(v) => set("product", v)}
                      options={[
                        { value: "", label: "Select a product…" },
                        ...products.map((p) => ({ value: p.slug, label: p.name })),
                        { value: "__other__", label: "Other / not listed" },
                      ]}
                    />
                    {values.product === "__other__" && (
                      <Field id="quote-product_other" label="Product (describe)" value={values.product_other} onChange={(v) => set("product_other", v)} />
                    )}
                  </>
                )}

                <Field id="quote-subject" label="Subject" required value={values.subject} onChange={(v) => set("subject", v)} error={errors.subject} />

                <TextareaField
                  id="quote-message"
                  label="Message or Requirement Details"
                  required
                  value={values.message}
                  onChange={(v) => set("message", v)}
                  error={errors.message}
                  maxLength={5000}
                />

                {/* Attachment */}
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy/70 font-semibold mb-2">Attachment (optional)</label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-mist-soft cursor-pointer hover:border-mist-gold text-sm text-mist-navy">
                      <Paperclip className="h-4 w-4" aria-hidden />
                      Choose file
                      <input ref={fileRef} id="quote-attachment" type="file" className="hidden" accept={ACCEPT_EXTS.map((e) => "." + e).join(",")} onChange={onFile} data-testid="quote-file-input" />
                    </label>
                    {file && (
                      <div className="flex items-center gap-2 text-sm text-mist-navy" data-testid="quote-file-info">
                        <span className="font-medium">{file.name}</span>
                        <span className="text-mist-navy/50">({(file.size / 1024).toFixed(0)} KB)</span>
                        <button type="button" onClick={clearFile} className="text-red-600 hover:text-red-700" aria-label="Remove file"><X className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-mist-navy/50">Accepted: {ACCEPT_EXTS.join(", ")}. Max {MAX_FILE_MB} MB. Not publicly accessible.</p>
                  {errors.attachment && <FieldError>{errors.attachment}</FieldError>}
                </div>

                {/* Consent */}
                <label className="flex items-start gap-3 text-sm" htmlFor="quote-consent">
                  <input
                    id="quote-consent"
                    type="checkbox"
                    checked={values.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-1 h-4 w-4 accent-mist-gold"
                    aria-invalid={!!errors.consent}
                    data-testid="quote-consent"
                  />
                  <span className="text-mist-navy/80 leading-relaxed">
                    I consent to MIST processing this request and contacting me about it. See our{" "}
                    <Link to="/privacy-policy" className="text-mist-navy underline underline-offset-4 hover:text-mist-gold">Privacy Policy</Link>.
                  </span>
                </label>
                {errors.consent && <FieldError>{errors.consent}</FieldError>}

                {status === "error" && (
                  <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" role="alert">
                    <AlertCircle className="h-4 w-4 mt-0.5" aria-hidden />
                    <span>{message}</span>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="quote-submit"
                    className="inline-flex items-center gap-2 bg-mist-gold text-mist-navy px-8 h-12 text-[11px] uppercase tracking-[0.22em] font-semibold rounded-md hover:bg-[#b8994f] transition-all duration-200 ease-out disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2 w-full sm:w-auto justify-center"
                  >
                    {status === "submitting" ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : ("Submit Request")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function FieldError({ children }) {
  return <div role="alert" className="mt-1.5 text-xs text-red-600">{children}</div>;
}

function Field({ id, label, required, type = "text", value, onChange, error }) {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="text-[11px] uppercase tracking-[0.22em] text-mist-navy/70 font-semibold mb-2">
        {label}{required && "*"}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`h-11 px-3.5 bg-white border rounded-md text-[15px] text-mist-ink placeholder:text-mist-ink/40 transition-all duration-200 ease-out focus:outline-none focus:border-mist-gold focus:ring-2 focus:ring-mist-gold/20 ${error ? "border-red-400" : "border-mist-soft"}`}
        data-testid={id}
      />
      {error && <FieldError><span id={`${id}-error`}>{error}</span></FieldError>}
    </div>
  );
}

function SelectField({ id, label, required, value, onChange, options, error }) {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="text-[11px] uppercase tracking-[0.22em] text-mist-navy/70 font-semibold mb-2">
        {label}{required && "*"}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`h-11 px-3 bg-white border rounded-md text-[15px] text-mist-ink transition-all duration-200 ease-out focus:outline-none focus:border-mist-gold focus:ring-2 focus:ring-mist-gold/20 ${error ? "border-red-400" : "border-mist-soft"}`}
        data-testid={id}
      >
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
      {error && <FieldError><span id={`${id}-error`}>{error}</span></FieldError>}
    </div>
  );
}

function TextareaField({ id, label, required, value, onChange, error, maxLength }) {
  return (
    <div className="flex flex-col">
      <div className="flex items-baseline justify-between mb-2">
        <label htmlFor={id} className="text-[11px] uppercase tracking-[0.22em] text-mist-navy/70 font-semibold">{label}{required && "*"}</label>
        {maxLength && <span className="text-xs text-mist-navy/50">{(value || "").length}/{maxLength}</span>}
      </div>
      <textarea
        id={id}
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`px-3.5 py-3 bg-white border rounded-md text-[15px] text-mist-ink placeholder:text-mist-ink/40 transition-all duration-200 ease-out focus:outline-none focus:border-mist-gold focus:ring-2 focus:ring-mist-gold/20 resize-y ${error ? "border-red-400" : "border-mist-soft"}`}
        data-testid={id}
      />
      {error && <FieldError><span id={`${id}-error`}>{error}</span></FieldError>}
    </div>
  );
}
